import {
  Box,
  HStack,
  VStack,
  Text,
  IconButton,
  NativeSelect,
  Separator,
  Field,
} from '@chakra-ui/react'
import { Select } from 'chakra-react-select'
import { FiPlus, FiX } from 'react-icons/fi'
import { useScriptPicker, useTagPicker, useTurfMapPicker } from '../../hooks/useCampaign'
import type { AlternateScriptConditionType, SaveAlternateScriptRequest } from '../../types/campaign'

export interface ScriptConfigValue {
  defaultScriptId: string | null
  alternates: SaveAlternateScriptRequest[]
}

interface Props {
  campaignId: string
  value: ScriptConfigValue
  onChange: (next: ScriptConfigValue) => void
  disabled?: boolean
}

interface SelectOption {
  value: string
  label: string
}

/** Save-time shaping shared by every caller: rows with no script are dropped and priority is the
 *  row order. (Editing keeps half-filled rows in `value`; only the wire shape is cleaned.) */
export function toSaveAlternates(alternates: SaveAlternateScriptRequest[]): SaveAlternateScriptRequest[] {
  return alternates
    .filter((a) => a.scriptId)
    .map((a, i) => ({
      scriptId: a.scriptId,
      conditionType: a.conditionType,
      conditionTagId: a.conditionType === 'Tag' ? a.conditionTagId ?? null : null,
      conditionTurfMapId: a.conditionType === 'TurfMap' ? a.conditionTurfMapId ?? null : null,
      priority: i,
    }))
}

/** Controlled default-script + alternates editor (extracted from CampaignScriptCard). The parent
 *  owns load/save; this only edits `value`. */
export function ScriptConfigForm({ campaignId, value, onChange, disabled }: Props) {
  const { data: scripts } = useScriptPicker(campaignId)
  const { data: tags } = useTagPicker(campaignId)
  const { data: turfMaps } = useTurfMapPicker(campaignId)

  const { defaultScriptId, alternates } = value

  const scriptOptions: SelectOption[] =
    scripts?.map((s) => ({ value: s.id, label: s.label })) ?? []

  const tagOptions: SelectOption[] =
    tags?.map((t) => ({ value: t.id, label: t.label })) ?? []

  const turfMapOptions: SelectOption[] =
    turfMaps?.map((tm) => ({ value: tm.id, label: tm.label })) ?? []

  const setAlternates = (next: SaveAlternateScriptRequest[]) =>
    onChange({ defaultScriptId, alternates: next })

  const addAlternate = () => {
    setAlternates([
      ...alternates,
      { scriptId: '', conditionType: 'Tag', conditionTagId: null, conditionTurfMapId: null, priority: alternates.length },
    ])
  }

  const removeAlternate = (index: number) => {
    setAlternates(alternates.filter((_, i) => i !== index))
  }

  const updateAlternate = (index: number, updates: Partial<SaveAlternateScriptRequest>) => {
    setAlternates(alternates.map((a, i) => (i === index ? { ...a, ...updates } : a)))
  }

  return (
    <>
      {/* Default Script */}
      <Field.Root mb={4}>
        <Field.Label fontSize="sm" fontWeight="medium">
          Default Script
        </Field.Label>
        <Select<SelectOption>
          size="sm"
          placeholder="Select or search scripts..."
          isClearable
          isSearchable
          isDisabled={disabled}
          options={scriptOptions}
          value={scriptOptions.find((o) => o.value === defaultScriptId) ?? null}
          onChange={(opt) => onChange({ defaultScriptId: opt?.value ?? null, alternates })}
          chakraStyles={{
            container: (base) => ({ ...base, maxW: '400px' }),
          }}
        />
      </Field.Root>

      {/* Alternate Scripts */}
      <Separator mb={3} />
      <HStack justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="medium" color="gray.600">
          Alternate Scripts
        </Text>
        <IconButton
          aria-label="Add alternate script"
          size="xs"
          colorPalette="brand"
          variant="ghost"
          disabled={disabled}
          onClick={addAlternate}><FiPlus /></IconButton>
      </HStack>

      {alternates.length === 0 && (
        <Text fontSize="xs" color="gray.400" pl={4} mb={2}>
          No alternate scripts configured. Click + to add one.
        </Text>
      )}

      <VStack gap={3} align="stretch">
        {alternates.map((alt, index) => (
          <Box
            key={index}
            pl={4}
            py={3}
            px={4}
            bg="gray.50"
            borderRadius="md"
            borderLeft="3px solid"
            borderLeftColor="info.fg"
          >
            <HStack gap={3} align="flex-end" flexWrap="wrap">
              {/* Script selector */}
              <Field.Root flex={2} minW="180px">
                <Field.Label fontSize="xs" color="gray.500">
                  Script
                </Field.Label>
                <Select<SelectOption>
                  size="sm"
                  placeholder="Select script..."
                  isSearchable
                  isDisabled={disabled}
                  options={scriptOptions}
                  value={
                    scriptOptions.find((o) => o.value === alt.scriptId) ?? null
                  }
                  onChange={(opt) =>
                    updateAlternate(index, {
                      scriptId: opt?.value ?? '',
                    })
                  }
                />
              </Field.Root>

              {/* Condition type (a NativeSelect must not sit inside Field.Root: it would override its own disabled) */}
              <Box w="130px" flexShrink={0}>
                <Text asChild fontSize="xs" color="gray.500" mb={1} display="block">
                  <label htmlFor={`alt-when-${index}`}>When</label>
                </Text>
                <NativeSelect.Root size="sm" disabled={disabled}>
                  <NativeSelect.Field
                    id={`alt-when-${index}`}
                    value={alt.conditionType}
                    onChange={(e) =>
                      updateAlternate(index, {
                        conditionType: e.target
                          .value as AlternateScriptConditionType,
                        conditionTagId: null,
                        conditionTurfMapId: null,
                      })
                    }
                  >
                    <option value="Tag">Tag</option>
                    <option value="TurfMap">Turf Map</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Box>

              {/* Condition value */}
              <Field.Root flex={2} minW="180px">
                <Field.Label fontSize="xs" color="gray.500">
                  {alt.conditionType === 'Tag' ? 'Has Tag' : 'In Turf Map'}
                </Field.Label>
                {alt.conditionType === 'Tag' ? (
                  <Select<SelectOption>
                    size="sm"
                    placeholder="Select tag..."
                    isSearchable
                    isClearable
                    isDisabled={disabled}
                    options={tagOptions}
                    value={
                      tagOptions.find(
                        (o) => o.value === alt.conditionTagId
                      ) ?? null
                    }
                    onChange={(opt) =>
                      updateAlternate(index, {
                        conditionTagId: opt?.value ?? null,
                      })
                    }
                  />
                ) : (
                  <Select<SelectOption>
                    size="sm"
                    placeholder="Select turf map..."
                    isSearchable
                    isClearable
                    isDisabled={disabled}
                    options={turfMapOptions}
                    value={
                      turfMapOptions.find(
                        (o) => o.value === alt.conditionTurfMapId
                      ) ?? null
                    }
                    onChange={(opt) =>
                      updateAlternate(index, {
                        conditionTurfMapId: opt?.value ?? null,
                      })
                    }
                  />
                )}
              </Field.Root>

              {/* Remove button */}
              <IconButton
                aria-label="Remove alternate"
                size="sm"
                variant="ghost"
                colorPalette="red"
                disabled={disabled}
                onClick={() => removeAlternate(index)}
                alignSelf="flex-end"
                mb="1px"><FiX /></IconButton>
            </HStack>
          </Box>
        ))}
      </VStack>
    </>
  )
}
